<div class="flex flex-col gap-y-20" [formGroup]="store.fundingForm">
  <!-- Two columns: HOW MUCH on the left, HOW IT TRAVELS on the right.

       They are genuinely independent decisions -- neither answer constrains the other -- so
       stacking them made the step scroll for no reason on a modal that is already 132rem wide.
       Side by side, the whole instruction is legible at once.

       `grid` rather than two flexed halves, for the divider: grid items stretch to the row
       height, so the rule on the right column spans the full height of the taller side. Two
       flex children would each be their own height and the rule would stop short.

       Collapses to one column below `md`, where the rule becomes the horizontal separator this
       step had before -- see the right column's border classes. -->
  <div class="grid grid-cols-1 md:grid-cols-2">
    <!-- ============ Amount ============ -->
    <!-- `pr-30` pairs with the right column's `pl-30` so the divider sits centred in a 60px
         gutter rather than hugging one side. -->
    <section class="flex flex-col gap-y-5 md:pr-30">
      <h3 class="text-lg font-bold text-gray-darkest">{{ heading() }}</h3>

      @if (store.isDistribution()) {
        <!-- Close Account leads the card rather than sitting at the bottom, because ticking it
             changes what the rest of the step asks for -- and what the whole wizard contains. A
             checkbox that silently removes three steps belongs where it is read first. -->
        <div class="flex items-start px-10 py-10 mb-5 border rounded gap-x-10"
          [class.border-gray-lighter]="!store.isCloseAccount()"
          [class.bg-gray-lightest]="!store.isCloseAccount()"
          [class.border-red-lighter]="store.isCloseAccount()"
          [class.bg-red-lightest]="store.isCloseAccount()">
          <div class="flex flex-col gap-y-8">
            <label class="flex items-center text-sm font-semibold cursor-pointer gap-x-8 text-gray-darkest">
              <input type="checkbox" formControlName="isCloseAccount" />
              Close this account
            </label>
            <span class="text-xs text-gray">
              Every position is liquidated and the full account value is distributed. The models,
              positions and cash steps no longer apply.
            </span>

            @if (store.isCloseAccount()) {
              <!-- Revealed only when closing, because it is meaningless otherwise. Defaults to
                   CHECKED, matching the v1 close-account workflow's `billAccountOnClosing`, so
                   moving to V2 does not silently reverse the answer for anyone. -->
              <label class="flex items-center pt-8 mt-2 text-sm cursor-pointer gap-x-8 text-gray-darkest border-t border-red-lighter">
                <input type="checkbox" formControlName="isCloseDateBilled" />
                Bill the account on the close date
              </label>
            }
          </div>
        </div>
      }

      @if (store.isCloseAccount()) {
        <!-- No amount input: the amount IS the account. Shown read-only so the advisor can see the
             figure the request will carry, captioned as an estimate because it moves with the
             market between now and settlement. -->
        <div class="flex flex-col gap-y-2">
          <span class="text-xs uppercase text-gray">Amount to be distributed</span>
          <span class="text-xl font-bold text-gray-darkest" style="font-variant-numeric: tabular-nums">
            {{ store.fundingAmount() | currency }}
          </span>
          <span class="text-xs text-gray">
            The full account value as at load. The final figure follows the liquidation.
          </span>
        </div>
      } @else {
        @if (store.isDistribution()) {
          <!-- GROSS / NET. A convenience only: the request stores one amount, and it is always
               what LEAVES the account. See `fundingAmount` in the store. -->
          <div class="flex flex-col gap-y-5">
            <span class="text-xs uppercase text-gray">
              Would you like to enter the GROSS or NET amount?
            </span>
            <div class="flex gap-x-2">
              <button type="button" class="button is-sm"
                [class.is-mint]="!store.isNetEntry()"
                [class.is-inverted]="store.isNetEntry()"
                [class.is-outlined]="store.isNetEntry()"
                (click)="setEntryMode('gross')">GROSS</button>
              <button type="button" class="button is-sm"
                [class.is-mint]="store.isNetEntry()"
                [class.is-inverted]="!store.isNetEntry()"
                [class.is-outlined]="!store.isNetEntry()"
                (click)="setEntryMode('net')">NET</button>
            </div>
          </div>
        }

        <!-- ⚠️ MARKED WHILE EMPTY, not after being touched (AndyP, 2026-09-22). The usual rule
             is to stay quiet until the advisor has engaged with a field, and it is wrong here:
             this step opens with two empty required fields and a disabled Next whose single
             message names only the first of them, so the advisor fixes the amount, presses Next,
             and only then learns about the bank account. Marking both from the start is what
             makes the step's shape visible.

             🟢 Not an ERROR state. It says "still needed", which is why it clears the moment a
             figure is entered rather than waiting for a blur, and why an amount that is merely
             too large is not marked here -- that has its own message. -->
        <label class="mt-10 text-xs uppercase text-gray" for="funding-amount">{{ amountLabel() }}</label>
        <div class="flex items-center gap-x-5">
          <span class="text-gray-darkest">$</span>
          <input id="funding-amount" type="number" min="0" formControlName="amount"
            appSelectOnFocus appCommitOnEnter appTwoDecimals
            class="w-200 text-right input no-spinner"
            [class.border-red-dark]="store.fundingAmountMissing()"
            [attr.aria-invalid]="store.fundingAmountMissing() ? 'true' : null"
            [attr.aria-describedby]="store.fundingAmountMissing() ? 'funding-amount-required' : null"
            [attr.aria-label]="amountLabel()"
            (change)="onAmount($event)" />
          @if (store.fundingAmountMissing()) {
            <span id="funding-amount-required" class="text-xs text-red-dark">Required</span>
          }
        </div>

      }

      <!-- ⚠️ OUTSIDE the close/amount branch above, deliberately. This used to sit inside the
           @else, so ticking Close Account skipped the withholding fields along with the amount
           input — and a close is exactly when withholding matters most, since the whole account
           is going out (AndyP, 2026-09-23).

           🟢 The STORE always handled it: `federalTaxPct` gates on `isDistribution()` alone, and
           `netAmount` derives from `fundingAmount`, which on a close is the account total. Only
           the inputs were missing, so nothing about the arithmetic changes here. -->
      @if (store.isDistribution()) {
        <!-- ============ Withholding ============ -->
        <div class="flex flex-wrap mt-15 gap-x-30 gap-y-10">
          <div class="flex flex-col gap-y-2">
            <span class="text-xs uppercase text-gray">Federal Tax</span>
            <span class="text-xs text-gray">
              CURRENT: {{ currentTaxCaption(store.funding()?.federalTaxWithholding) }}
            </span>
            <div class="flex items-center gap-x-5">
              <input type="number" min="0" max="100" formControlName="federalTaxPct"
                appSelectOnFocus appCommitOnEnter appTwoDecimals
                class="w-120 text-right input is-sm no-spinner" placeholder="0.00"
                aria-label="Federal tax withholding percent"
                (change)="onFederalTax($event)" />
              <span class="text-gray-darkest">%</span>
            </div>
          </div>
          <div class="flex flex-col gap-y-2">
            <span class="text-xs uppercase text-gray">State Tax</span>
            <span class="text-xs text-gray">
              CURRENT: {{ currentTaxCaption(store.funding()?.stateTaxWithholding) }}
            </span>
            <div class="flex items-center gap-x-5">
              <input type="number" min="0" max="100" formControlName="stateTaxPct"
                appSelectOnFocus appCommitOnEnter appTwoDecimals
                class="w-120 text-right input is-sm no-spinner" placeholder="0.00"
                aria-label="State tax withholding percent"
                (change)="onStateTax($event)" />
              <span class="text-gray-darkest">%</span>
            </div>
          </div>
        </div>

        @if (store.taxWithholdingChanged()) {
          <!-- The V1 wording, kept verbatim: it is a standing consequence the advisor has to be
               told about, not a validation message. Non-blocking, hence orange. -->
          <div class="flex items-start px-10 py-8 mt-10 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              <strong>Please Note</strong>, if you are changing withholding elections, they will
              remain in effect on all distributions for this account until you change or revoke it.
            </span>
          </div>
        }

        @if (store.fundingAmount() > 0) {
          <div class="px-15 py-10 mt-10 text-sm border rounded border-mint-lighter bg-mint-lightest">
              <!-- ⚠️ SUPPRESSED ON A CLOSE, where the amount is already on screen
                   read-only a few lines up. A figure printed twice on one screen invites
                   the reader to check whether the two agree. The NET line below is the
                   part a close still needs, and it is the reason this panel follows the
                   tax fields out of the else. -->
              @if (!store.isCloseAccount()) {
                <p class="text-gray-darkest">
                  <span class="font-bold text-mint-darker">{{ store.fundingAmount() | currency }}</span>
                  will be distributed from this account.
                </p>
              }
            <p class="mt-5 text-gray-darkest">
              Your client will receive
              <span class="font-bold text-mint-darker">{{ store.netAmount() | currency }}</span>
              after applicable tax withholdings.
            </p>
          </div>
        }
      }
    </section>

    <!-- ============ Delivery ============ -->
    <!-- One border, two orientations. Stacked (below md) it is the horizontal rule above this
         section, with the padding that goes with it; side by side it becomes the vertical
         divider between the columns and that padding is cancelled. -->
    <section class="flex flex-col pt-20 mt-20 border-t gap-y-15 border-gray-lightest
      md:pt-0 md:mt-0 md:border-t-0 md:border-l md:pl-30">
      <h3 class="text-lg font-bold text-gray-darkest">Delivery</h3>

      <!-- No "options could not be loaded" branch here on purpose: an empty or failed lookup is
           FATAL for these request types (DeliveryOptionsUnavailableError), so the wizard never
           reaches this step with an empty list — it shows the full-height load-error panel
           instead. A warning here would describe a state that cannot occur. -->
      <div class="flex flex-col gap-y-5">
        <!-- The label carries the info icon and the reason when the method is fixed, which is V1's
             treatment: a greyed select sitting on ACH with the explanation on hover, rather than a
             one-item dropdown that reads as a choice the advisor is failing to make. -->
        <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="delivery-method">
          Please choose a {{ requestNoun() }} method
          @if (store.deliveryMethodLocked()) {
            <i class="material-icons text-mint-dark" style="font-size: 1.4rem; cursor: help"
              [title]="lockedMethodReason()">info</i>
          }
        </label>
        <!-- A native `disabled`, not V1's pointer-events/opacity pair. This select is bound with
             (change) rather than formControlName, so disabling the ELEMENT leaves the form's value
             intact and submittable -- which is the whole reason V1 reached for the CSS trick. A
             real disabled attribute is also reachable by assistive tech, where
             pointer-events: none simply makes the control silently inert.

             🔴 `[selected]` on the OPTIONS, never `[value]` on the select. A native select's
             `value` property is applied before its `@for` options exist, so the browser has
             nothing to match and falls back to showing the FIRST option. That read as "Select a
             method" while ACH was in fact selected in the store -- and made the gate look absent,
             because Next was correctly enabled against a method the advisor could not see. Same
             pattern the model-allocation dropdown uses.

             No placeholder option: ACH is pre-selected on load for both request types, so
             "no method chosen" is not a state the advisor can reach or needs offered. -->
        <select id="delivery-method" class="select w-320"
          [disabled]="store.deliveryMethodLocked()"
          (change)="onMethod($event)">
          @for (method of store.deliveryMethods(); track method.id) {
            <option [value]="method.id" [selected]="method.id === store.selectedDeliveryMethod()?.id">
              {{ method.name }}
            </option>
          }
        </select>
        @if (store.deliveryMethodLocked()) {
          <span class="text-xs text-gray">{{ lockedMethodReason() }}</span>
        }
      </div>

      @if (store.selectedDeliveryMethod() && store.usesBankInstruction()) {
        <div class="flex flex-col gap-y-5">
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="standing-instruction">
            Bank account
            <!-- ⚠️ On the LABEL here, where the amount carries it beside the input. The select is
                 320px of mostly empty space with its placeholder on the left, so a hint at its
                 right edge reads as unrelated to the words it qualifies. -->
            @if (store.bankAccountMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <!-- Keeps its placeholder, unlike the two selects around it: there is no sensible
               default bank account, and picking one for the advisor would be choosing where a
               client's money goes.

               ⚠️ Which is exactly why it is marked: the other two selects open on a usable
               default, so this is the only one on the panel that is genuinely outstanding, and
               nothing about a placeholder distinguishes "not chosen" from "chosen and shown". -->
          <select id="standing-instruction" class="select w-320"
            [class.border-red-dark]="store.bankAccountMissing()"
            [attr.aria-invalid]="store.bankAccountMissing() ? 'true' : null"
            (change)="onStandingInstruction($event)">
            <option value="" [selected]="store.selectedStandingInstruction() === null">
              Select bank account
            </option>
            @for (instruction of store.filteredStandingInstructions(); track instruction.key) {
              <option [value]="instruction.key"
                [selected]="instruction.key === store.selectedStandingInstruction()?.key">
                {{ instruction.displayValue }}
              </option>
            }
          </select>
        </div>

        @if (store.funding()?.standingInstructionsFailed) {
          <!-- 🔴 Distinct from "none on file", and deliberately so. A failed lookup that rendered
               as an empty list would tell the advisor to file custodial paperwork for
               authorizations that may well already exist. -->
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              This account's bank authorizations could not be loaded, so they are not shown. Close
              the request and try again rather than assuming none are on file.
            </span>
          </div>
        } @else if (showNoInstructionsWarning() || showNoAuthorityWarning()) {
          <!-- The V1 copy, verbatim, including the Schwab link -- the advisor's next action is
               unchanged and this is the wording they already know. -->
          <div class="flex items-start px-10 py-8 text-xs border rounded gap-x-8 border-orange-lighter bg-orange-lightest text-orange-darker">
            <i class="material-icons" style="font-size: 1.4rem">info_outline</i>
            <span>
              <strong>No Standing Authorization on account for this selection.</strong><br />
              In order to establish a new transfer/payment, please submit the necessary custodial
              paperwork.<br />
              <a class="underline" href="api/sso/schwab?destination=D_DASH" target="_blank"
                rel="noopener" aria-label="Schwab Advisor Center">Go to Schwab Advisor Center</a>
            </span>
          </div>
        }
      }

      <!-- Only a check can be sent overnight, so the option appears only for a check-delivered
           method. Keyed on the method's Schwab transaction-type code, which arrives with the row.
           ⚠️ Reachable on distributions only in practice, since a contribution is locked to ACH —
           but written against the method rather than the request type, because that is what the
           question is actually about. -->
      @if (store.isCheckDelivery()) {
        <label class="flex items-center text-sm cursor-pointer gap-x-8 text-gray-darkest">
          <input type="checkbox" formControlName="isOvernightDelivery" />
          Send the check overnight
        </label>
      }

      <div class="flex flex-col gap-y-5">
        <!-- Carries the info icon when the frequency is fixed, exactly as the delivery method
             above does. Two locked selects on one step should not look like two different
             mechanisms. -->
        <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="delivery-frequency">
          How often is the {{ requestNoun() }} made?
          @if (store.deliveryFrequencyLocked()) {
            <i class="material-icons text-mint-dark" style="font-size: 1.4rem; cursor: help"
              [title]="lockedFrequencyReason()">info</i>
          }
        </label>
        <!-- Also placeholder-free: One Time is pre-selected on load, matching V1's default, so
             the common case needs no interaction and no start date.

             /!\ LOCKED ON A CLOSE, not hidden -- it was hidden for about an hour (AndyP,
             2026-09-23). The frequency is filed either way and the trade team reads it, so a
             control that disappears turns a stated answer into an invisible one. Greyed out on
             One Time says the same thing in the place the reader looks for it.

             The store rewrites the control when Close Account is ticked, so the value shown here
             IS the value submitted -- the `[selected]` binding needs no special case. A native
             `disabled` for the same reason the method select uses one: it leaves the form value
             intact and submittable, where pointer-events/opacity only looks disabled. -->
        <select id="delivery-frequency" class="select w-320"
          [disabled]="store.deliveryFrequencyLocked()"
          (change)="onFrequency($event)">
          @for (frequency of store.deliveryFrequencies(); track frequency.id) {
            <option [value]="frequency.id" [selected]="frequency.id === store.selectedDeliveryFrequency()?.id">
              {{ frequency.name }}
            </option>
          }
        </select>
        @if (store.deliveryFrequencyLocked()) {
          <span class="text-xs text-gray">{{ lockedFrequencyReason() }}</span>
        }
      </div>

      <!-- Shown only for a repeating schedule: a one-time transfer has nothing to begin. Keyed on
           the frequency's own flag rather than on its name or id.
           Never on a close, which is locked to One Time -- see `startDateRequired`. -->
      @if (store.startDateRequired()) {
        <div class="flex flex-col gap-y-5">
          <!-- Same treatment as the amount and the bank account: this is required whenever it is
               rendered at all, and an empty datepicker looks no different from an optional one. -->
          <label class="flex items-center text-xs uppercase gap-x-5 text-gray" for="start-date">
            When should the {{ requestNoun() }} initiate?
            @if (store.startDateMissing()) {
              <span class="normal-case text-red-dark">Required</span>
            }
          </label>
          <!-- The shared `.datepicker-input-container` recipe (styles.input.scss:377): a relative
               container, an input with right padding, and an ABSOLUTELY positioned icon sitting in
               that padding. Same markup shape as V1's delivery step, so the two look identical.

               ⚠️ Was a `<mat-datepicker-toggle matIconSuffix>`, which is why the icon hung outside
               the box: `matIconSuffix` only does anything inside a `<mat-form-field>`, and there
               is none here, so the attribute was inert and the toggle laid out as a block element
               AFTER the input instead of within it.

               A ligature icon rather than V1's `<mat-icon svgIcon="calendar">`: it needs neither
               MatIconModule nor an entry in the SVG registry, and every other icon in this wizard
               is a ligature. `.datepicker-icon` already supplies the positioning and `mi-18`. -->
          <div class="datepicker-input-container w-320">
            <input id="start-date" class="datepicker-input" formControlName="startDate" readonly
              placeholder="mm/dd/yyyy"
              [class.border-red-dark]="store.startDateMissing()"
              [attr.aria-invalid]="store.startDateMissing() ? 'true' : null"
              [matDatepicker]="picker" [min]="store.minStartDate()" (click)="picker.open()" />
            <!-- `pointer-events: none` so a click on the icon falls through to the input, which is
                 what opens the picker. Without it the icon is a dead patch in the middle of a
                 control that is otherwise clickable everywhere -- the one papercut V1 has here. -->
            <i class="material-icons datepicker-icon" style="pointer-events: none"
              aria-hidden="true">calendar_today</i>
            <mat-datepicker #picker color="accent" />
          </div>
        </div>
      }
    </section>

  </div>

  <!-- ⚠️ No blocked-reason message here on purpose. It is rendered once, beside Next, by the
       wizard shell (`reallocation-v2-request.component.html`) from this same computed. Showing it
       in both places put the identical sentence twice on one screen, which reads as two problems
       and makes the advisor look for a second thing to fix.

       Beside Next is the right of the two homes: that is the control it disables, and this step's
       two columns have no single place a step-level message belongs to -- under the left column it
       looked like a complaint about the amount even when the delivery details were at fault. -->
</div>
